/* eslint-disable react/no-danger */
/* eslint-disable import/no-extraneous-dependencies */
import { BellOutlined, CheckCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Badge, Button, Empty, Modal, Popover, Spin, Tag, Tooltip } from 'antd';
import URL from 'constant/url';
import { useEffect, useState } from 'react';
import HTMLString from 'react-html-string';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { getReadNotificationIds, markAllNotificationsAsRead, markNotificationAsRead } from 'util/notificationStorage';
import { actionGetAnnouncements, IInfo, selectLoadingNotification, selectLoadingNotificationInfo } from './slice';

export default function Notification() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const listNotification = useAppSelector(selectLoadingNotificationInfo);
  const statusLoad = useAppSelector(selectLoadingNotification);

  const [openPopover, setOpenPopover] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<IInfo | null>(null);
  const [readIds, setReadIds] = useState<number[]>([]);

  useEffect(() => {
    dispatch(actionGetAnnouncements());
    setReadIds(getReadNotificationIds());
  }, [dispatch]);

  useEffect(() => {
    setReadIds(getReadNotificationIds());
  }, [listNotification]);

  const validNotifications = Array.isArray(listNotification)
    ? listNotification.filter((item) => !item.hide && Boolean(item.description))
    : [];

  const unreadCount = validNotifications.filter((item) => item.id && !readIds.includes(item.id)).length;

  const handleOpenPopover = (open: boolean) => {
    setOpenPopover(open);
    if (open && validNotifications.length > 0) {
      // Khi người dùng bấm vào xem thông báo thì đánh dấu đã đọc để không hiện số nữa
      const allIds = validNotifications.map((item) => item.id).filter(Boolean) as number[];
      const updated = markAllNotificationsAsRead(allIds);
      setReadIds(updated);
    }
  };

  const handleItemClick = (item: IInfo) => {
    if (item.id) {
      const updated = markNotificationAsRead(item.id);
      setReadIds(updated);
    }
    setOpenPopover(false);
    setSelectedNotification(item);
  };

  const handleMarkAllRead = () => {
    const allIds = validNotifications.map((item) => item.id).filter(Boolean) as number[];
    const updated = markAllNotificationsAsRead(allIds);
    setReadIds(updated);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const renderNotificationList = () => {
    if (statusLoad === 'loading') {
      return (
        <div className="py-8 flex justify-center items-center">
          <Spin size="small" />
        </div>
      );
    }
    if (validNotifications.length === 0) {
      return (
        <div className="py-8">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có thông báo mới" />
        </div>
      );
    }
    return validNotifications.map((item) => {
      const isRead = item.id ? readIds.includes(item.id) : true;
      return (
        <div
          key={item.id || item.description}
          className={`p-3.5 hover:bg-blue-50/60 cursor-pointer transition-colors duration-150 flex flex-col gap-1 ${
            isRead ? 'bg-white' : 'bg-blue-50/25 font-medium'
          }`}
          onClick={() => handleItemClick(item)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleItemClick(item);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5 line-clamp-1">
              {!isRead && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
              <span
                className={`text-sm hover:text-blue-600 ${isRead ? 'text-gray-700' : 'text-gray-900 font-semibold'}`}
              >
                {item.description}
              </span>
            </div>
            {item.created && (
              <span className="text-[11px] text-gray-400 whitespace-nowrap flex-shrink-0">
                {formatDate(item.created)}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 line-clamp-2 pl-3.5">
            <HTMLString html={item.content} />
          </div>
        </div>
      );
    });
  };

  const popoverContent = (
    <div className="w-80 sm:w-96 max-w-[90vw] -m-2">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-base text-gray-800">Thông báo</span>
          {unreadCount > 0 && (
            <Tag color="blue" className="rounded-full px-2 text-xs">
              {unreadCount} mới
            </Tag>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Tooltip title="Đánh dấu tất cả đã đọc">
              <Button
                type="text"
                size="small"
                icon={<CheckCircleOutlined className="text-blue-500" />}
                onClick={handleMarkAllRead}
              />
            </Tooltip>
          )}
          <Button
            type="link"
            size="small"
            className="text-xs p-0 text-blue-600"
            onClick={() => {
              setOpenPopover(false);
              navigate(URL.Notification);
            }}
          >
            Xem tất cả
          </Button>
        </div>
      </div>

      <div className="max-h-[360px] overflow-y-auto divide-y divide-gray-100">{renderNotificationList()}</div>

      {validNotifications.length > 0 && (
        <div className="p-2 border-t border-gray-100 bg-gray-50/50 text-center">
          <Button
            type="text"
            block
            size="small"
            className="text-xs text-blue-600 font-medium flex items-center justify-center gap-1"
            onClick={() => {
              setOpenPopover(false);
              navigate(URL.Notification);
            }}
          >
            Đi tới trang tất cả thông báo <RightOutlined style={{ fontSize: 10 }} />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Popover
        content={popoverContent}
        trigger="click"
        placement="bottomRight"
        open={openPopover}
        onOpenChange={handleOpenPopover}
        arrow={{ pointAtCenter: true }}
      >
        <div className="flex items-center justify-center cursor-pointer p-1.5 rounded-full hover:bg-gray-100 transition-colors">
          <Badge count={unreadCount} size="small" offset={[-2, 2]}>
            <BellOutlined style={{ fontSize: 21, color: '#4b5563' }} />
          </Badge>
        </div>
      </Popover>

      <Modal
        title={<span className="text-base font-semibold text-blue-600">{selectedNotification?.description}</span>}
        open={Boolean(selectedNotification)}
        onOk={() => setSelectedNotification(null)}
        onCancel={() => setSelectedNotification(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setSelectedNotification(null)}>
            Đóng
          </Button>,
        ]}
      >
        <div className="py-2">
          {selectedNotification?.created && (
            <div className="text-xs text-gray-400 mb-3">Thời gian: {formatDate(selectedNotification.created)}</div>
          )}
          <div className="prose max-w-none text-sm text-gray-700 leading-relaxed border-t pt-3">
            <HTMLString html={selectedNotification?.content || 'Không có nội dung chi tiết'} />
          </div>
        </div>
      </Modal>
    </>
  );
}
